"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { AuthCredentialKind, AuthSuccess } from "@/components/auth/types";
import { patientClaimQueryKey } from "@/hooks/patient-claim/use-patient-claim";
import { toast } from "@/lib/use-toast";
import { CandidatesResponse, PatientClaimAuthMode } from "@/types/patient-claim";
import { SelfSchedulePatient } from "@/types/self-schedule";
import { listPatientCandidates } from "@/api/auth/patient-claim";
import { authService } from "@/services/auth-service";
export interface UseSelfScheduleAuthSuccessOptions {
  onResolved: (patient: SelfSchedulePatient) => void;
  /** Passive observer: never used to decide authentication or navigation. */
  onAccessConfirmed?: (credential: AuthSuccess) => void;
  /**
   * Continue a sign-in with no patient at this clinic into sign-up on the
   * current surface. Return true when handled; returning false (or omitting
   * this) keeps the "No patient linked" error.
   */
  onUnlinkedPatient?: (credential: AuthSuccess) => boolean | Promise<boolean>;
}

/**
 * Post-auth resolver for the self-schedule MfaDialog. Branches on the
 * auth method that produced the credential:
 *
 *   - PHONE   → the phone-claim mutation already produced the Patient;
 *               just refresh the token and surface it.
 *   - PASSWORD / GOOGLE → portal-style sign-in. Refresh the token, then
 *               fetch the patient-claim response (now portal-typed) and
 *               surface the single linked Patient.
 *
 * Unlike `usePostAuthRouter(PATIENT_PORTAL_LOGIN)`, this composer does
 * NOT redirect to /patient-portal — the user is mid-booking.
 */
function observeAccess(observer: ((credential: AuthSuccess) => void) | undefined, credential: AuthSuccess): void {
  try {
    observer?.(credential);
  } catch {
    // Passive observers must never prevent a verified patient from continuing.
  }
}
export function useSelfScheduleAuthSuccess(t0) {
  const $ = _c(5);
  const {
    onResolved,
    onUnlinkedPatient,
    onAccessConfirmed
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== onAccessConfirmed || $[1] !== onResolved || $[2] !== onUnlinkedPatient || $[3] !== queryClient) {
    t1 = async (credential, t2) => {
      const isActive = t2 === undefined ? _temp : t2;
      if (!isActive()) {
        return;
      }
      if (credential.kind === AuthCredentialKind.PHONE) {
        try {
          await authService.refreshAuthToken();
        } catch {}
        if (!isActive()) {
          return;
        }
        observeAccess(onAccessConfirmed, credential);
        onResolved(credential.patient);
        return;
      }
      if (credential.kind !== AuthCredentialKind.PASSWORD && credential.kind !== AuthCredentialKind.GOOGLE) {
        return;
      }
      const isCurrent = () => isActive() && authService.getCurrentUser()?.uid === credential.user.uid;
      if (!isCurrent()) {
        return;
      }
      try {
        await authService.refreshAuthToken();
      } catch {}
      if (!isCurrent()) {
        return;
      }
      if (credential.patient) {
        observeAccess(onAccessConfirmed, credential);
        onResolved(credential.patient);
        return;
      }
      const uid = credential.user.uid;
      const data = await queryClient.fetchQuery({
        queryKey: patientClaimQueryKey(uid),
        queryFn: _temp2
      });
      if (!isCurrent()) {
        return;
      }
      if (data?.authMode === PatientClaimAuthMode.PORTAL) {
        observeAccess(onAccessConfirmed, credential);
        onResolved(data.patient);
        return;
      }
      if (data?.authMode === PatientClaimAuthMode.NONE && (await onUnlinkedPatient?.(credential))) {
        if (!isActive()) {
          return;
        }
        queryClient.removeQueries({
          queryKey: patientClaimQueryKey(uid),
          exact: true
        });
        return;
      }
      if (!isCurrent()) {
        return;
      }
      toast({
        title: "No patient linked",
        description: "This account isn't linked to a patient. Please sign up for the patient portal first.",
        variant: "error"
      });
      try {
        await authService.logout(true);
      } catch {}
    };
    $[0] = onAccessConfirmed;
    $[1] = onResolved;
    $[2] = onUnlinkedPatient;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function _temp2(t0) {
  const {
    signal
  } = t0;
  return listPatientCandidates(signal);
}
function _temp() {
  return true;
}